/**
 * Jepto Table Card Defaults
 *
 * Default configuration for the Highcharts Grid Pro table component.
 *
 * THEMING: All visual styling is handled via Highcharts Grid Pro CSS variables (--hcg-*)
 * which are mapped to Jepto theme variables in jepto-table-card.component.scss.
 * See: https://www.highcharts.com/docs/grid/theming/theming-variables
 *
 * PAGINATION: See https://highcharts.com/docs/grid/pagination
 */

/**
 * Complete default configuration for Jepto Table Card
 */
export const JeptoTableCardDefaults = {
	// =====================================================
	// Column Defaults
	// =====================================================
	columnDefaults: {
		sorting: { enabled: true },
		minWidth: 80,
		headerAlign: 'left' as const,
	},

	// =====================================================
	// Rendering Configuration
	// =====================================================
	rendering: {
		/** Jepto theme on `.hcg-container` — omit `hcg-theme-default` to disable built-in zebra. */
		theme: 'jepto-table-grid-theme',
		rows: {
			virtualization: false,
			/** Allow multi-line cells (e.g. stacked period comparison). */
			strictHeights: false,
			/** Grid Pro default (2) pads tbody min-height; disable for compact short pages. */
			minVisibleRows: null,
		},
		columns: {
			resizing: {
				enabled: true,
				mode: 'distributed' as const,
			},
		},
		table: {
			className: 'jepto-table-grid',
		},
	},

	// =====================================================
	// Pagination Configuration
	// See: https://highcharts.com/docs/grid/pagination
	// =====================================================
	pagination: {
		// Enable pagination by default
		enabled: true,

		// Default rows per page (Grid Pro default is 10)
		pageSize: 10,

		// Position: 'bottom' | 'top' | 'footer' | '#custom-element-id'
		position: 'bottom' as const,

		// Pagination UI controls
		controls: {
			// Page size dropdown selector
			pageSizeSelector: {
				enabled: true,
				options: [10, 25, 50, 100],
			},

			// "Showing X - Y of Z (page N of M)" info text
			pageInfo: {
				enabled: true,
			},

			// First (<<) and Last (>>) page buttons
			firstLastButtons: {
				enabled: false,
			},

			// Previous (<) and Next (>) page buttons
			previousNextButtons: {
				enabled: true,
			},

			// Page number buttons (1, 2, 3, etc.)
			pageButtons: {
				enabled: true,
				count: 5, // Number of page buttons visible at once
			},
		},
	},

	// =====================================================
	// Styling
	// =====================================================
	styling: {
		height: '100%',
		width: '100%',
	},

	// =====================================================
	// Credits
	// =====================================================
	credits: {
		enabled: false,
	},
};

// Type inference from the defaults object
export type JeptoTableCardConfig = typeof JeptoTableCardDefaults;

/** Compact Grid Pro `lang.pagination.pageInfo` (no page X of Y clause). */
export const TABLE_PAGINATION_PAGE_INFO = 'Showing {start}–{end} of {total}';

export interface BuildPaginationConfigOptions {
	/** When true, keeps page info only; hides page-size selector and nav. */
	singlePage?: boolean;
}

export function tablePaginationTotalPages(
	totalRows: number,
	pageSize: number
): number {
	if (totalRows <= 0) {
		return 1;
	}
	return Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));
}

/**
 * Build Grid Pro pagination config from Jepto defaults
 * Converts our config structure to Grid Pro's expected format
 */
export function buildPaginationConfig(
	pagination: JeptoTableCardConfig['pagination'],
	options?: BuildPaginationConfigOptions
): Record<string, unknown> | false {
	if (!pagination.enabled) {
		return false;
	}

	const singlePage = options?.singlePage === true;
	const showPageNavigation = !singlePage;

	return {
		enabled: true,
		pageSize: pagination.pageSize,
		position: pagination.position,
		controls: {
			pageSizeSelector:
				!singlePage && pagination.controls.pageSizeSelector.enabled
					? { options: pagination.controls.pageSizeSelector.options }
					: false,
			pageInfo: pagination.controls.pageInfo.enabled,
			firstLastButtons: showPageNavigation
				? pagination.controls.firstLastButtons.enabled
				: false,
			previousNextButtons: showPageNavigation
				? pagination.controls.previousNextButtons.enabled
				: false,
			pageButtons:
				showPageNavigation && pagination.controls.pageButtons.enabled
					? { count: pagination.controls.pageButtons.count }
					: false,
		},
	};
}
